import camelCase from 'voca/camel_case';
import { z } from 'zod';

import { keys } from './keys.ts';
import { programCountryLabels } from './sac-values.ts';

export const cmsOrganizationsOptions = {
  intendedForReuse: 'intendedForReuse',
  intentionallyOrphaned: 'intentionallyOrphaned',
} as const;

export const cmsOrganizations = [
  cmsOrganizationsOptions.intendedForReuse,
  cmsOrganizationsOptions.intentionallyOrphaned,
] as const;

export const cmsOrganizationsMap = Object.fromEntries(
  cmsOrganizations.map((value) => [value, value]),
) as { [K in (typeof cmsOrganizations)[number]]: string };

export const contentOwners = [
  'b2bBusinessToBusiness',
  'b2cBusinessToConsumer',
  'churchAndInfluencer',
  'digitalMarketing',
  'eventsAndVolunteers',
  'gpeGlobalPhilanthropicEngagement',
  'informationLegal',
  'marketingTechnology',
  'mtxMarketingTransformationExperience',
  'pcPeopleAndCulture',
  'prPublicRelations',
  'radio',
  'supporterCare',
  'supporterExperience',
  'tripsAndVisits',
  'unknownContentOwner',
] as const;

export const ContentOwnersEnum = z.enum(contentOwners);
export type ContentOwnersEnum = z.infer<typeof ContentOwnersEnum>;

export const contentOwnerLabels: { [key in ContentOwnersEnum]: string } = {
  b2bBusinessToBusiness: 'B2B: Business to Business',
  b2cBusinessToConsumer: 'B2C: Business to Consumer',
  churchAndInfluencer: 'Church and Influencer',
  digitalMarketing: 'Digital Marketing',
  eventsAndVolunteers: 'Events and Volunteers',
  gpeGlobalPhilanthropicEngagement: 'GPE: Global Philanthropic Engagement',
  informationLegal: 'Information/Legal',
  marketingTechnology: 'Marketing Technology',
  mtxMarketingTransformationExperience:
    'MTX: Marketing Transformation Experience',
  pcPeopleAndCulture: 'P&C: People and Culture',
  prPublicRelations: 'PR: Public Relations',
  radio: 'Radio',
  supporterCare: 'Supporter Care',
  supporterExperience: 'Supporter Experience',
  tripsAndVisits: 'Trips and Visits',
  unknownContentOwner: 'Unknown Content Owner',
};

export const articleThemes = [
  'alumni',
  'childDevelopment',
  'christmas',
  'church',
  'community',
  'conflict',
  'culture',
  'disaster',
  'discipleship',
  'easter',
  'economicDevelopment',
  'education',
  'family',
  'health',
  'humanRights',
  'letters',
  'mentalHealth',
  'nutrition',
  'prayer',
  'recipes',
  'sanitationAndHygiene',
  'survival',
  'water',
  'worldDays',
] as const;

export const ArticleThemesEnum = z.enum(articleThemes);
export type ArticleThemesEnum = z.infer<typeof ArticleThemesEnum>;

export const articleTopics = [
  'aboutCompassion',
  'childSponsorship',
  'christianFaith',
  'forSponsors',
  'impact',
  'poverty',
] as const;

export const ArticleTopicsEnum = z.enum(articleTopics);
export type ArticleTopicsEnum = z.infer<typeof ArticleTopicsEnum>;

export const articleCountries = Object.values(programCountryLabels).map(
  (label) => camelCase(label),
) as [
  (typeof programCountryLabels)[keyof typeof programCountryLabels],
  ...(typeof programCountryLabels)[keyof typeof programCountryLabels][],
];

export const ArticleCountriesEnum = z.enum(articleCountries);
export type ArticleCountriesEnum = z.infer<typeof ArticleCountriesEnum>;

const ArticleTermsEnumOptions = [
  ...ArticleThemesEnum.options,
  ...ArticleTopicsEnum.options,
  ...ArticleCountriesEnum.options,
] as const;

export const ArticleTermsEnum = z.enum(ArticleTermsEnumOptions);
export type ArticleTermsEnum = z.infer<typeof ArticleTermsEnum>;

export const articleCategoryOptionsMap = {
  topic: ArticleTopicsEnum.options,
  theme: ArticleThemesEnum.options,
  country: ArticleCountriesEnum.options,
} as const;

export const articleCategories = ['topic', 'theme', 'country'] as const;

export const ArticleCategoriesEnum = z.enum(articleCategories);
export type ArticleCategoriesEnum = z.infer<typeof ArticleCategoriesEnum>;

export const funnel = ['step1', 'step2', 'step3', 'step4', 'step5'] as const;
export const FunnelEnum = z.enum(funnel);
export type FunnelEnum = z.infer<typeof FunnelEnum>;
export const funnelLabels: { [key in FunnelEnum]: string } = {
  step1: 'Reach',
  step2: 'Nurture',
  step3: 'Convert',
  step4: 'Engage',
  step5: 'Delight',
};

export const articleTypes = [
  keys.articleTypes.blogPost,
  keys.articleTypes.crisisUpdate,
  keys.articleTypes.pressRelease,
] as const;

export const ArticleTypesEnum = z.enum(articleTypes);
export type ArticleTypesEnum = z.infer<typeof ArticleTypesEnum>;

export const fundCategories = [
  'basicNeeds',
  'communityCareAndDevelopment',
  'educationAndClasses',
  'emergencySupport',
  'food',
  'healthAndMedical',
  'homeAndShelter',
  'jobsAndIncome',
  'livestockAndAgriculture',
  'moms',
  'relief',
  'technology',
];

export const fundPromotions = [
  'giftCatalog',
  'mostNeeded',
  'popular',
  'urgent',
] as const;

export const FundPromotionsEnum = z.enum(fundPromotions);
export type FundPromotionsEnum = z.infer<typeof FundPromotionsEnum>;

export const personalizationSegments = ['largeDonor', 'currentSponsor'];

export const otherTaxonomies = ['promotedContent'];
